import { useCallback, useEffect, useRef, useState } from 'react'
import styles from './HoldToTransmit.module.css'

/**
 * HOLD-TO-TRANSMIT — the mic key (desk-grammar brief §4.2).
 *
 * Press and hold to dictate; on release the transcribed words land in the
 * composer for the operator to read before sending. Confirm-first by design: the
 * desk never puts something on the wire that you have not seen.
 *
 * TEXT IN, ALWAYS. Recognition is the browser's own (on-device or per platform);
 * this component records nothing, stores nothing, and transmits no audio. What
 * leaves the desk is text, exactly as the engine expects.
 *
 * Renders nothing where the API is unavailable — typing remains the path, and an
 * inert control would be worse than no control (§4.2's graceful fallback).
 */

// The Web Speech API is not in lib.dom's standard surface. Declared minimally
// here rather than pulling a dependency or widening the app's types.
interface SpeechRecognitionAlternativeLike {
  transcript: string
}
interface SpeechRecognitionResultLike {
  isFinal: boolean
  0: SpeechRecognitionAlternativeLike
}
interface SpeechRecognitionEventLike {
  resultIndex: number
  results: {
    length: number
    [i: number]: SpeechRecognitionResultLike
  }
}
interface SpeechRecognitionLike {
  continuous: boolean
  interimResults: boolean
  onresult: ((e: SpeechRecognitionEventLike) => void) | null
  onerror: (() => void) | null
  onend: (() => void) | null
  start: () => void
  stop: () => void
  abort: () => void
}
type SpeechRecognitionCtor = new () => SpeechRecognitionLike

function getRecognitionCtor(): SpeechRecognitionCtor | null {
  if (typeof window === 'undefined') return null
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecognitionCtor
    webkitSpeechRecognition?: SpeechRecognitionCtor
  }
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null
}

export default function HoldToTransmit({
  onTranscript,
  disabled,
}: {
  /** Called on release with the dictated text, for the composer to hold. */
  onTranscript: (text: string) => void
  disabled?: boolean
}) {
  const [supported] = useState(() => getRecognitionCtor() !== null)
  const [live, setLive] = useState(false)

  const recognition = useRef<SpeechRecognitionLike | null>(null)
  const finalText = useRef('')
  /**
   * Whether the key is still under the thumb. THE iOS FIX: Safari does not honour
   * `continuous` — recognition ends at the first pause however long you hold —
   * so `onend` restarts it while this is true. Without that, a hold goes dead
   * after the first sentence and the label flips off under the player's finger,
   * which reads as broken rather than as a platform limit.
   *
   * A ref, not state: `onend` is a callback on an object created in `start`, and
   * a state read there would be the value from the render that created it.
   */
  const holding = useRef(false)
  // Held in a ref so the recognition callbacks never close over a stale prop.
  const emit = useRef(onTranscript)
  emit.current = onTranscript

  const stop = useCallback(() => {
    holding.current = false
    setLive(false)
    const r = recognition.current
    if (!r) return
    recognition.current = null
    try {
      r.stop()
    } catch {
      /* already stopped */
    }
    const text = finalText.current.trim()
    finalText.current = ''
    if (text) emit.current(text)
  }, [])

  const start = useCallback(() => {
    if (disabled || live) return
    const Ctor = getRecognitionCtor()
    if (!Ctor) return

    const r = new Ctor()
    r.continuous = true
    r.interimResults = true
    finalText.current = ''

    r.onresult = (e) => {
      // Accumulate finalized segments only; interim results are re-emitted as
      // they firm up and would otherwise duplicate.
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const res = e.results[i]
        if (res.isFinal) finalText.current += res[0].transcript
      }
    }
    r.onerror = () => stop()
    r.onend = () => {
      // Still held? Safari ended on a pause, not because the player let go —
      // start listening again and keep the accumulated transcript. The label
      // stays ON AIR throughout, because from the desk's side nothing stopped.
      if (holding.current && recognition.current === r) {
        try {
          r.start()
          return
        } catch {
          /* refused to restart — fall through and settle honestly */
        }
      }
      setLive(false)
    }

    recognition.current = r
    holding.current = true
    try {
      r.start()
      setLive(true)
    } catch {
      recognition.current = null
      holding.current = false
    }
  }, [disabled, live, stop])

  // Abort rather than stop on unmount: no stray transcript should land in a
  // composer that is no longer on screen.
  useEffect(
    () => () => {
      const r = recognition.current
      recognition.current = null
      holding.current = false
      try {
        r?.abort()
      } catch {
        /* nothing to abort */
      }
    },
    [],
  )

  if (!supported) return null

  return (
    <button
      type="button"
      className={`${styles.key} ${live ? styles.live : ''}`}
      disabled={disabled}
      aria-label={live ? 'Transmitting — release to stop' : 'Hold to transmit'}
      aria-pressed={live}
      onPointerDown={(e) => {
        // Capture so a finger sliding off the key still ends the transmission.
        e.currentTarget.setPointerCapture?.(e.pointerId)
        start()
      }}
      onPointerUp={stop}
      onPointerCancel={stop}
      // Holding a button natively opens the context menu on touch; suppress it.
      onContextMenu={(e) => e.preventDefault()}
    >
      <span className={styles.dot} aria-hidden="true" />
      <span className={styles.label}>{live ? 'ON AIR' : 'HOLD'}</span>
    </button>
  )
}
